/**
 * PublicLeagueTeamPage — /l/:slug/team/:teamId
 * ─────────────────────────────────────────────────────────────────
 * REDESIGNED in the public pages overhaul P3 (2026-06-09). The
 * dedicated per-team page:
 *
 *   • Header: team name, division-season, W-L-T record, and the
 *     "Subscribe to schedule" calendar button (secure per-team iCal
 *     feed — webcal link + copy URL; refreshes every 6 hours).
 *   • Team news: blog posts written by the team's coach/manager
 *     (league_posts with team_id), with images.
 *   • Schedule & results: the team's season game log with W/L/T
 *     score badges.
 *   • Roster: privacy-filtered (initials for minors / non-opted-in
 *     adults — enforced server-side).
 */

import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import { renderMarkdown } from '../../utils/tinyMarkdown.jsx';
import {
  container, Section, Card, Chip, EmptyState,
  INK, MUTED, LINE, SURFACE, NAVY,
  formatDateNice, formatTimeNice, resultBadge, accentText,
} from './pubUi.jsx';

export default function PublicLeagueTeamPage() {
  const { league, accent } = useOutletContext();
  const { slug, teamId } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null); setMissing(false);
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/teams/${teamId}`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setMissing(true); });
    return () => { cancelled = true; };
  }, [slug, teamId]);

  useEffect(() => {
    if (data?.team?.name && league?.name) {
      document.title = `${data.team.name} — ${league.name}`;
    }
  }, [data?.team?.name, league?.name]);

  if (missing) {
    return (
      <div style={{ ...container, paddingTop: 60, textAlign: 'center', color: MUTED }}>
        <div style={{ fontSize: 34 }}>🏒</div>
        <h1 style={{ color: INK }}>Team not found</h1>
        <Link to={`/l/${slug}/teams`} style={{ color: accent, fontWeight: 800, textDecoration: 'none' }}>← All teams</Link>
      </div>
    );
  }
  if (!data) {
    return <div style={{ ...container, paddingTop: 60, color: MUTED }}>Loading…</div>;
  }

  const { team, season, record, games = [], roster = [], posts = [], ics_url: icsUrl } = data;
  const absoluteIcs = icsUrl ? `${window.location.origin}${icsUrl}` : null;
  const webcal = absoluteIcs ? absoluteIcs.replace(/^https?:/, 'webcal:') : null;

  async function copyIcs() {
    if (!absoluteIcs) return;
    try {
      await navigator.clipboard.writeText(absoluteIcs);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard denied — the webcal link still works */ }
  }

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const upcoming = games.filter(g => !g.is_scoresheet_locked && g.game_date >= todayStr);
  const played = games.filter(g => g.is_scoresheet_locked && g.home_score != null).reverse();

  return (
    <div>
      {/* ── Team header band ─────────────────────────────────── */}
      <div style={{ background: NAVY, color: '#fff', padding: '40px 0 34px', marginBottom: 34 }}>
        <div style={{ ...container, display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
          <span style={{
            width: 72, height: 72, borderRadius: 18, flexShrink: 0,
            background: accent, color: accentText(accent),
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 900, fontSize: 26,
          }}>
            {team.name.slice(0, 2).toUpperCase()}
          </span>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.7 }}>
              {league.name}{season ? ` · ${season.name}` : ''}
            </div>
            <h1 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 900, letterSpacing: '-0.02em', margin: '4px 0 8px' }}>
              {team.name}
            </h1>
            {record && (
              <span style={{
                padding: '5px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.25)', fontWeight: 800, fontSize: 13,
              }}>
                {record.w}-{record.l}{record.t ? `-${record.t}` : ''} <span style={{ opacity: 0.7, fontWeight: 600 }}>W-L{record.t ? '-T' : ''}</span>
              </span>
            )}
          </div>
          {/* Calendar subscribe — the secure 6-hour-refresh iCal feed */}
          {absoluteIcs && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
              <a href={webcal} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '11px 18px', borderRadius: 12, background: accent,
                color: accentText(accent), fontWeight: 800, fontSize: 14, textDecoration: 'none',
              }}>
                📅 Subscribe to schedule
              </a>
              <button type="button" onClick={copyIcs} style={{
                background: 'none', border: '1px solid rgba(255,255,255,0.3)',
                color: 'rgba(255,255,255,0.85)', borderRadius: 10, padding: '7px 12px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>
                {copied ? '✓ Link copied' : 'Copy calendar link'}
              </button>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                Auto-updates every 6 hours
              </span>
            </div>
          )}
        </div>
      </div>

      <div style={container}>
        {/* ── Team news ─────────────────────────────────────────── */}
        <Section eyebrow="From the bench" title="Team news">
          {posts.length === 0 ? (
            <EmptyState icon="📣">No team updates posted yet.</EmptyState>
          ) : (
            posts.map(p => (
              <Card key={p.id} style={{ marginBottom: 18, overflow: 'hidden' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {p.image_url && (
                    <img src={p.image_url} alt=""
                         style={{ width: 280, minHeight: 180, objectFit: 'cover', flex: '0 0 280px' }} />
                  )}
                  <div style={{ padding: '20px 24px', flex: '1 1 320px', minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                      {p.pinned && <Chip color="#9a6700" bg="#fff3d1">Pinned</Chip>}
                      <span style={{ fontSize: 12, color: MUTED }}>
                        {formatDateNice(p.created_at)}{p.author_name ? ` · ${p.author_name}` : ''}
                      </span>
                    </div>
                    <h3 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 800, color: INK }}>{p.title}</h3>
                    <div style={{ fontSize: 14.5, lineHeight: 1.65, color: '#2a3147' }}>
                      {p.body_markdown ? renderMarkdown(p.body_markdown) : null}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </Section>

        {/* ── Schedule + results ────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, marginBottom: 44 }}>
          <Section eyebrow="Coming up" title="Schedule" style={{ marginBottom: 0 }}>
            {upcoming.length === 0 ? (
              <EmptyState icon="🗓">No upcoming games.</EmptyState>
            ) : (
              <Card>
                {upcoming.map((g, i) => (
                  <Link key={g.id} to={`/l/${slug}/game/${g.id}`} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    borderBottom: i < upcoming.length - 1 ? `1px solid ${LINE}` : 'none',
                    color: INK, textDecoration: 'none',
                  }}>
                    <div style={{ width: 86, flexShrink: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 13 }}>{formatDateNice(g.game_date)}</div>
                      <div style={{ fontSize: 12, color: MUTED }}>{formatTimeNice(g.game_time)}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}>
                      <OpponentLine g={g} teamId={team.id} />
                      {g.venue_name && <div style={{ fontSize: 12, color: MUTED }}>{g.venue_name}</div>}
                    </div>
                  </Link>
                ))}
              </Card>
            )}
          </Section>

          <Section eyebrow="How it went" title="Results" style={{ marginBottom: 0 }}>
            {played.length === 0 ? (
              <EmptyState icon="🥅">No final scores yet.</EmptyState>
            ) : (
              <Card>
                {played.map((g, i) => {
                  const badge = resultBadge(g, team.id);
                  return (
                    <Link key={g.id} to={`/l/${slug}/game/${g.id}`} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                      borderBottom: i < played.length - 1 ? `1px solid ${LINE}` : 'none',
                      color: INK, textDecoration: 'none',
                    }}>
                      <div style={{ width: 86, flexShrink: 0, fontSize: 12, color: MUTED }}>
                        {formatDateNice(g.game_date)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, fontSize: 13.5 }}>
                        <OpponentLine g={g} teamId={team.id} />
                      </div>
                      {badge && (
                        <span style={{ fontWeight: 900, fontSize: 13.5, color: badge.color, whiteSpace: 'nowrap' }}>
                          {badge.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </Card>
            )}
          </Section>
        </div>

        {/* ── Roster ────────────────────────────────────────────── */}
        <Section eyebrow="Who's playing" title="Roster">
          {roster.length === 0 ? (
            <EmptyState icon="🧊">
              The roster appears here once the team's game sheets have been finalized.
            </EmptyState>
          ) : (
            <Card style={{ padding: '8px 0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                {roster.map((p, i) => (
                  <div key={p.profile_id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px' }}>
                    <span style={{
                      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                      background: SURFACE, display: 'inline-flex', alignItems: 'center',
                      justifyContent: 'center', fontWeight: 800, fontSize: 12, color: accent,
                    }}>
                      {(p.name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: 14, color: INK }}>{p.name || '—'}</span>
                    {p.position && <span style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase' }}>{p.position.slice(0, 1)}</span>}
                  </div>
                ))}
              </div>
            </Card>
          )}
          <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
            Player names follow the league's privacy settings — minors always appear as initials.
          </div>
        </Section>
      </div>
    </div>
  );
}

function OpponentLine({ g, teamId }) {
  const home = g.home_team_id === teamId;
  const opponent = home ? g.away_team_name : g.home_team_name;
  return (
    <div style={{ fontWeight: 700 }}>
      <span style={{ color: MUTED, fontWeight: 600 }}>{home ? 'vs' : '@'}</span> {opponent}
    </div>
  );
}
